import React, { useEffect, useRef } from "react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const toolbarButtonClass =
  "px-2 py-1 text-sm border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A]";

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Write here...",
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (editor.innerHTML !== value) {
      editor.innerHTML = value || "";
    }
  }, [value]);

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const runCommand = (command: string, commandValue?: string) => {
    focusEditor();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current?.innerHTML || "");
  };

  const escapeHtml = (input: string): string =>
    input
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const addLink = () => {
    const selectionText = window.getSelection()?.toString().trim() || "";
    const titleInput = window.prompt("Link Title", selectionText);
    if (!titleInput) return;

    const urlInput = window.prompt("Link URL (https://...)", "https://");
    if (!urlInput) return;

    const title = escapeHtml(titleInput.trim());
    const url = urlInput.trim();

    if (!title || !url) return;

    focusEditor();
    const safeUrl = url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;
    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    if (!range || !editorRef.current?.contains(range.commonAncestorContainer)) {
      return;
    }

    // Replace current selection with a dedicated anchor node,
    // then place caret after a trailing space so typing continues outside link.
    range.deleteContents();

    const anchor = document.createElement("a");
    anchor.href = safeUrl;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.textContent = title;

    const trailingSpace = document.createTextNode(" ");

    range.insertNode(trailingSpace);
    range.insertNode(anchor);

    const newRange = document.createRange();
    newRange.setStartAfter(trailingSpace);
    newRange.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(newRange);

    onChange(editorRef.current?.innerHTML || "");
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <div className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-lg overflow-hidden">
      <div className="p-2 flex flex-wrap items-center gap-2 bg-gray-50 dark:bg-[#FFFFFF1A]">
        <button
          type="button"
          className={toolbarButtonClass}
          onClick={() => runCommand("bold")}
        >
          B
        </button>
        <button
          type="button"
          className={toolbarButtonClass}
          onClick={() => runCommand("italic")}
        >
          I
        </button>
        <button
          type="button"
          className={toolbarButtonClass}
          onClick={() => runCommand("underline")}
        >
          U
        </button>
        <button type="button" className={toolbarButtonClass} onClick={addLink}>
          Link
        </button>
        <button
          type="button"
          className={toolbarButtonClass}
          onClick={() => runCommand("unlink")}
        >
          Unlink
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        title={placeholder}
        className="min-h-[140px] p-3 outline-none dark:text-white"
      />
    </div>
  );
};

export default RichTextEditor;
